import { ArrowRight, Globe, Code, Database, Share2, Download, RotateCw, UserCheck, Shield, Router, FileCode, BarChart2, Tag, DollarSign, CheckCircle, FileText, MessageSquare, Eye, Clock, Search, ShoppingCart, Heart, Home, Shirt, Diamond, Car, Monitor, Gift, Watch, Building, Plane, Users, Layers, Star } from "lucide-react";
import { cn, createStaggerAnimationDelay } from "@/lib/utils";
import { useState } from "react";

const Technology = () => {
  // Example product for the "Standardized Product Data" section
  const exampleProductData = {
    name: "Dior Addict Lip Glow - Moisturizing lip balm",
    price: "42.00",
    currency: "EUR",
    availability: "In stock",
    description: "First color-enhancing lip balm by Dior, Dior Addict Lip Glow enhances the lips with a natural color by reacting to their chemistry.",
    mpn: "Y0996458",
    brand: "Dior",
    breadcrumb: "Beauty > Makeup > Lips > Lip balm",
    imageUrl: "/lovable-uploads/e3eff5ce-65ce-4f2c-a9dc-247714e8ad23.png",
    rating: "4.7/5 (124 reviews)",
    productUrl: "https://www.dior.com/en_us/products/beauty-Y0996458",
    pagination: "Page 1 of 3",
    customerReviews: "\"I love it! Moisturizing and the shade is perfect\" - Claire M."
  };

  // Function to get the favicon URL for a website
  const getFaviconUrl = (domain: string) => {
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  };

  return (
    <section className="py-12 md:py-24 relative overflow-hidden bg-white dark:bg-benchr-900/30">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-benchr-400/5 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-benchr-600/5 rounded-full blur-3xl -z-10" />
      
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="text-center mb-8 md:mb-16 max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-benchr-900 dark:text-white mb-3 md:mb-4 tracking-tight animate-slide-up">
            Comprehensive Data Access
          </h2>
          <p className="text-base sm:text-lg text-benchr-600 dark:text-benchr-300 animate-slide-up px-2" style={{ animationDelay: "100ms" }}>
            Discover our comprehensive data collection network that provides access to product, pricing, and market data from any e-commerce site worldwide.
          </p>
        </div>

        {/* Data Catalog Section - Improved for mobile */}
        <div className="max-w-6xl mx-auto mb-10 md:mb-20 bg-benchr-900 dark:bg-benchr-950 rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
          <div className="p-4 sm:p-6 md:p-8">
            <div className="flex flex-col lg:flex-row justify-between items-start gap-5 md:gap-8">
              <div className="w-full lg:max-w-xl">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4">
                  Global E-commerce Data Catalog
                </h3>
                <p className="text-benchr-200 text-sm sm:text-base md:text-lg mb-4 md:mb-6 leading-relaxed">
                  Subscribe to our data feeds covering any e-commerce website worldwide. We handle all the data collection, cleaning, and processing, so you can focus on analyzing the insights without worrying about infrastructure.
                </p>
                
                {/* Stats badges - Improved for mobile */}
                <div className="flex flex-wrap gap-3 mb-5 md:mb-8">
                  <div className="bg-benchr-700/50 rounded-full px-4 sm:px-5 py-2 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-benchr-300" />
                    <span className="text-sm sm:text-base font-semibold text-white">+5 million</span>
                    <span className="text-xs sm:text-sm text-benchr-300">websites</span>
                  </div>
                  <div className="bg-benchr-700/50 rounded-full px-4 sm:px-5 py-2 flex items-center gap-2">
                    <Database className="w-4 h-4 text-benchr-300" />
                    <span className="text-sm sm:text-base font-semibold text-white">+1 billion</span>
                    <span className="text-xs sm:text-sm text-benchr-300">products</span>
                  </div>
                </div>
                
                {/* CTA Button - Updated with Book a Demo */}
                <div className="mb-6 md:mb-8">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(
                        'https://calendar.google.com/calendar/appointments/schedules/AcZssZ0ixREziCGTZSnjXhuPB0TBIti2X5Ks-ATre2wgAkuMuHqJ4XWW7hldUQxV7WW_GMwHwd9IwfBm?gv=true',
                        'BookADemo',
                        'width=1400,height=900'
                      );
                    }}
                    className="group inline-flex items-center justify-center px-6 py-3.5 bg-[#1e70d1] hover:bg-[#1e70d1]/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-300 ease-in-out w-full sm:w-auto pulse-soft"
                  >
                    Book a Demo
                    <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
              
              {/* Data Sources - Only show Sephora.com on mobile */}
              <div className="w-full lg:w-auto">
                {/* Only show one card on mobile, full grid on larger screens */}
                <div className="hidden sm:grid sm:grid-cols-2 gap-3">
                  {[
                    {
                      title: "Sephora.com",
                      description: "Complete product data: names, brands, prices, reviews, ratings, categories, images and more. Updated daily.",
                      views: "375+",
                      downloads: "124+"
                    },
                    {
                      title: "Aesop.com",
                      description: "Full product catalog data: names, descriptions, prices, variants, ingredients, categories, and images. Updated daily.",
                      views: "258+",
                      downloads: "96+"
                    },
                    {
                      title: "Dyson.com",
                      description: "Complete product information: models, prices, features, specifications, warranty details, reviews, and more. Updated daily.",
                      views: "296+",
                      downloads: "87+"
                    },
                    {
                      title: "Uniqlo.com",
                      description: "Complete product data: codes, names, colors, sizes, materials, prices, images, reviews, stock status, and more. Updated daily.",
                      views: "312+",
                      downloads: "104+"
                    },
                  ].map((dataSource, index) => (
                    <div key={index} className="relative bg-benchr-800/50 border border-benchr-700/30 rounded-lg p-4 transition-all hover:bg-benchr-800/70 hover:border-benchr-600/40 hover:shadow-lg hover:-translate-y-1 duration-300">
                      <h4 className="text-white font-semibold text-base mb-2 flex items-center">
                        <img 
                          src={getFaviconUrl(dataSource.title)} 
                          alt={`${dataSource.title} favicon`} 
                          className="w-4 h-4 mr-2"
                        />
                        <a 
                          href={`https://${dataSource.title}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:underline line-clamp-1"
                        >
                          {dataSource.title}
                        </a>
                      </h4>
                      <p className="text-benchr-300 text-sm mb-3 line-clamp-3">{dataSource.description}</p>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-3 text-xs mt-auto pt-1">
                        <div className="flex items-center text-benchr-400">
                          <Eye className="w-3 h-3 mr-1" />
                          <span>{dataSource.views}</span>
                        </div>
                        <div className="flex items-center text-benchr-400">
                          <Download className="w-3 h-3 mr-1" />
                          <span>{dataSource.downloads}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Mobile-only: Show just one card */}
                <div className="sm:hidden">
                  <div className="relative bg-benchr-800/50 border border-benchr-700/30 rounded-lg p-4 transition-all">
                    <h4 className="text-white font-semibold text-base mb-2 flex items-center">
                      <img 
                        src={getFaviconUrl("Sephora.com")} 
                        alt="Sephora.com favicon" 
                        className="w-4 h-4 mr-2"
                      />
                      <a 
                        href="https://Sephora.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:underline line-clamp-1"
                      >
                        Sephora.com
                      </a>
                    </h4>
                    <p className="text-benchr-300 text-sm mb-3">
                      Complete product data: names, brands, prices, reviews, ratings, categories, images and more. Updated daily.
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-3 text-xs mt-auto pt-1">
                      <div className="flex items-center text-benchr-400">
                        <Eye className="w-3 h-3 mr-1" />
                        <span>375+</span>
                      </div>
                      <div className="flex items-center text-benchr-400">
                        <Download className="w-3 h-3 mr-1" />
                        <span>124+</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* MERGED SECTION: Comprehensive Data Access for Developers and Non-Developers */}
        <div className="mt-10 md:mt-16 max-w-6xl mx-auto bg-white dark:bg-benchr-900/30 rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border border-benchr-100 dark:border-benchr-700/40">
          <div className="p-5 sm:p-6 md:p-8">
            <div className="text-center mb-6 md:mb-10">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-benchr-900 dark:text-white mb-3 md:mb-4">
                One Platform, Two Ways to Access
              </h3>
              <p className="text-base sm:text-lg text-benchr-600 dark:text-benchr-300 max-w-3xl mx-auto">
                Our platform delivers structured e-commerce data that's ready for immediate use by both technical and non-technical teams, with no infrastructure setup required.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mb-6 md:mb-12">
              {/* For Developers Panel */}
              <div className="relative bg-benchr-950 rounded-xl overflow-hidden border border-benchr-800/50">
                <div className="absolute top-0 right-0 -z-10 w-64 h-64 bg-gradient-to-br from-benchr-800/20 to-transparent rounded-bl-full"></div>
                
                <div className="p-5 sm:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-benchr-600 to-benchr-800 flex items-center justify-center">
                      <Code className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <h4 className="text-lg sm:text-xl font-bold text-white">For Developers</h4>
                  </div>
                  
                  <p className="text-benchr-300 mb-5 sm:mb-6 text-sm sm:text-md">
                    Access our comprehensive API with structured JSON data for direct integration into your systems and custom applications.
                  </p>
                  
                  <div className="bg-benchr-900/60 rounded-lg p-3 sm:p-4 mb-5 sm:mb-6 border border-benchr-800 overflow-hidden">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        </div>
                        <span className="text-xs text-benchr-400">JSON Response</span>
                      </div>
                      <div className="hidden sm:flex items-center gap-1.5">
                        <div className="text-xs text-benchr-500 bg-benchr-800 px-2 py-0.5 rounded">API</div>
                        <div className="text-xs text-benchr-500 bg-benchr-800 px-2 py-0.5 rounded">SDK</div>
                      </div>
                    </div>
                    
                    {/* Simple Code Example */}
                    <pre className="text-xs overflow-auto whitespace-pre-wrap sm:whitespace-pre text-green-400 mt-2 max-h-[120px] sm:max-h-none">
                      {`{
  "name": "Dior Addict Lip Glow",
  "price": 42.00,
  "currency": "EUR",
  "brand": "Dior",
  "mpn": "Y0996458",
  "reviews": [{
    "author": "Claire M.",
    "rating": 5,
    "comment": "I love it!"
  }]
}`}
                    </pre>
                  </div>
                  
                  {/* Benefits Items */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-benchr-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-benchr-400" />
                      </div>
                      <div>
                        <h5 className="text-white font-medium mb-0.5 sm:mb-1 text-sm sm:text-base">Complete Product Data</h5>
                        <p className="text-benchr-400 text-xs sm:text-sm">Access standardized product info, pricing, images, descriptions and more</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-benchr-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-benchr-400" />
                      </div>
                      <div>
                        <h5 className="text-white font-medium mb-0.5 sm:mb-1 text-sm sm:text-base">Multiple Integration Options</h5>
                        <p className="text-benchr-400 text-xs sm:text-sm">REST API, GraphQL API, SDKs for major languages and frameworks</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-benchr-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-benchr-400" />
                      </div>
                      <div>
                        <h5 className="text-white font-medium mb-0.5 sm:mb-1 text-sm sm:text-base">Flexible Data Delivery</h5>
                        <p className="text-benchr-400 text-xs sm:text-sm">On-demand access, scheduled exports, or real-time webhooks</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* For Non-Developers Panel */}
              <div className="relative bg-benchr-50 dark:bg-benchr-800 rounded-xl overflow-hidden border border-benchr-100 dark:border-benchr-700/40">
                <div className="absolute top-0 right-0 -z-10 w-64 h-64 bg-gradient-to-br from-benchr-100/60 dark:from-benchr-600/10 to-transparent rounded-bl-full"></div>
                
                <div className="p-5 sm:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-benchr-500 to-benchr-600 flex items-center justify-center">
                      <BarChart2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <h4 className="text-lg sm:text-xl font-bold text-benchr-900 dark:text-white">For Non-Developers</h4>
                  </div>
                  
                  <p className="text-benchr-600 dark:text-benchr-300 mb-5 sm:mb-6 text-sm sm:text-md">
                    Access the same powerful data through intuitive dashboards and downloadable reports, with no technical expertise required.
                  </p>
                  
                  {/* Visual Data Example */}
                  <div className="bg-white dark:bg-benchr-900/40 rounded-lg p-3 sm:p-4 mb-5 sm:mb-6 border border-benchr-200 dark:border-benchr-700/40 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <img 
                          src={getFaviconUrl("dior.com")} 
                          alt="Dior favicon" 
                          className="w-4 h-4"
                        />
                        <span className="text-xs sm:text-sm text-benchr-900 dark:text-benchr-300 font-medium">Dior Product Data</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="text-xs text-benchr-500 dark:text-benchr-400 bg-benchr-100 dark:bg-benchr-800 px-2 py-0.5 rounded">CSV</div>
                        <div className="text-xs text-benchr-500 dark:text-benchr-400 bg-benchr-100 dark:bg-benchr-800 px-2 py-0.5 rounded">Excel</div>
                      </div>
                    </div>
                    
                    {/* Simplified Product Card */}
                    <div className="flex gap-2 sm:gap-3 p-2 bg-benchr-50 dark:bg-benchr-800/50 rounded-md border border-benchr-100 dark:border-benchr-700/30">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white dark:bg-benchr-900 rounded-md flex items-center justify-center p-1">
                        <img 
                          src="/lovable-uploads/e3eff5ce-65ce-4f2c-a9dc-247714e8ad23.png" 
                          alt="Product" 
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h6 className="text-xs font-medium text-benchr-900 dark:text-white truncate">{exampleProductData.name}</h6>
                        <p className="text-xs text-benchr-600 dark:text-benchr-400">{exampleProductData.price} {exampleProductData.currency}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-3 h-3 text-yellow-500" />
                          <span className="text-xs text-benchr-500 dark:text-benchr-400">{exampleProductData.rating}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Benefits Items */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-benchr-200 dark:bg-benchr-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-benchr-600 dark:text-benchr-300" />
                      </div>
                      <div>
                        <h5 className="text-benchr-900 dark:text-white font-medium mb-0.5 sm:mb-1 text-sm sm:text-base">Ready-to-Use Reports</h5>
                        <p className="text-benchr-600 dark:text-benchr-400 text-xs sm:text-sm">Download pre-built reports in CSV, Excel, and PDF formats</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-benchr-200 dark:bg-benchr-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-benchr-600 dark:text-benchr-300" />
                      </div>
                      <div>
                        <h5 className="text-benchr-900 dark:text-white font-medium mb-0.5 sm:mb-1 text-sm sm:text-base">Interactive Dashboards</h5>
                        <p className="text-benchr-600 dark:text-benchr-400 text-xs sm:text-sm">Visualize product data with custom filters and comparisons</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-benchr-200 dark:bg-benchr-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-benchr-600 dark:text-benchr-300" />
                      </div>
                      <div>
                        <h5 className="text-benchr-900 dark:text-white font-medium mb-0.5 sm:mb-1 text-sm sm:text-base">Automated Insights</h5>
                        <p className="text-benchr-600 dark:text-benchr-400 text-xs sm:text-sm">AI-powered analyses highlighting trends and opportunities</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Technology;
